import { useEffect, useState } from "react";
import { Stethoscope } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bookdental/ui";
import { useAuth } from "@/lib/auth";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";
import { haQuantoTempo, useAtalhoDeAtendimento, type MeuAtendimento } from "@/lib/em-atendimento";

const pilula =
  "inline-flex h-9 shrink-0 items-center rounded-full border border-cyan-200 bg-cyan-50 text-xs font-semibold text-cyan-800 transition-colors hover:bg-cyan-100 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-300 dark:hover:bg-cyan-500/20";

/**
 * Atalho pro MEU atendimento em curso, no topo e no centro da tela — em qualquer organização.
 *
 * Existe porque quem está atendendo sai da ficha o tempo todo — consultar a agenda, ver outro
 * paciente, trocar de organização — e voltar exigia refazer o caminho. Atendimento de outra
 * organização leva o nome dela, e o clique troca para lá (com "Desfazer"): a ficha é da clínica.
 * Some quando não há atendimento e quando você já está dentro dele.
 */
export function AtendimentoEmCursoBotao({ compacto = false }: { compacto?: boolean }) {
  const atalho = useAtalhoDeAtendimento();
  const ativa = useAuth().user?.activeOrganization?.id;
  const { abrir } = useAbrirNaOrganizacao();

  // Redesenha o "há N min" sem depender de refetch.
  const [, redesenhar] = useState(0);
  useEffect(() => {
    const t = setInterval(() => redesenhar((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!atalho) return null;
  const { atual, outros } = atalho;

  // `abrir` só navega quando a organização já é a ativa, e troca (com "Desfazer") quando não é.
  const ir = (a: MeuAtendimento) => abrir(a.organizationId, a.organizationName, a.patientId ? `/pacientes/${a.patientId}?atendimento=${a.id}` : "/agenda");
  const deFora = (a: MeuAtendimento) => a.organizationId !== ativa;
  const tempo = haQuantoTempo(atual.startedAt);
  const primeiroNome = atual.patientName.split(/\s+/)[0];

  return (
    <span className="inline-flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => ir(atual)}
        title={`Voltar ao atendimento de ${atual.patientName} · ${atual.organizationName}${tempo ? ` · ${tempo}` : ""}`}
        className={`${pilula} max-w-[280px] gap-1.5 px-2.5`}
      >
        <span className="relative flex h-2 w-2 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-500 opacity-60 motion-reduce:animate-none" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-600" />
        </span>
        {/* No modo compacto sobra só o essencial — o ponto pulsando e o primeiro nome. Ele é usado
            em cabeçalho disputado (a Agenda), onde cada 90px viram um controle da barra que deixa
            de caber; o tooltip do botão continua trazendo nome completo, clínica e tempo. */}
        {!compacto && <Stethoscope className="hidden h-3.5 w-3.5 shrink-0 sm:block" />}
        <span className="truncate">
          {!compacto && <span className="hidden sm:inline">Atendendo </span>}
          {primeiroNome}
          {/* De fora, a clínica vem mesmo no compacto: sem ela o atalho levaria pra outra organização sem avisar. */}
          {deFora(atual) && <span className="font-medium opacity-80"> · {atual.organizationName}</span>}
        </span>
        {tempo && !compacto && <span className="hidden shrink-0 font-medium opacity-70 md:inline">· {tempo}</span>}
      </button>
      {outros.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label={`Mais ${outros.length} ${outros.length === 1 ? "atendimento aberto" : "atendimentos abertos"}`} className={`${pilula} px-2`}>
              +{outros.length}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="z-[60] min-w-[260px] border border-zinc-200 bg-white p-1 shadow-xl dark:border-border dark:bg-card">
            <p className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">Seus atendimentos abertos</p>
            {[atual, ...outros].map((a) => (
              <DropdownMenuItem key={a.id} onSelect={() => ir(a)} className="flex cursor-pointer flex-col items-start gap-0 rounded-md px-2 py-1.5 outline-none data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-muted">
                <span className="text-[13px] font-medium text-zinc-900 dark:text-foreground">{a.patientName}</span>
                <span className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {a.organizationName}
                  {haQuantoTempo(a.startedAt) ? ` · ${haQuantoTempo(a.startedAt)}` : ""}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </span>
  );
}
